import { createContext, useContext, useState, ReactNode } from 'react';
import { Box, Typography, IconButton, Slide } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { colors } from '@src/styles';

interface ErrorContextType {
  setError: (error: ErrorData) => void;
  clearError: () => void;
  setSuccess: (data: SuccessData) => void;
}

type NotificationKind = 'error' | 'success';

interface BaseData {
  title?: string;
  message: string;
  details?: string;
  statusCode?: number;
}

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
interface ErrorData extends BaseData {}
// eslint-disable-next-line @typescript-eslint/no-empty-object-type
interface SuccessData extends BaseData {}

const ErrorContext = createContext<ErrorContextType | undefined>(undefined);

export const useError = () => {
  const context = useContext(ErrorContext);
  if (!context) {
    throw new Error('useError must be used within ErrorProvider');
  }
  return context;
};

interface ErrorProviderProps {
  children: ReactNode;
}

export const ErrorProvider = ({ children }: ErrorProviderProps) => {
  const [error, setErrorState] = useState<BaseData | null>(null);
  const [kind, setKind] = useState<NotificationKind>('error');
  const [isVisible, setIsVisible] = useState(false);

  const show = (data: BaseData, type: NotificationKind) => {
    setErrorState(data);
    setKind(type);
    setIsVisible(true);

    // Auto-hide after 4 seconds
    setTimeout(() => {
      setIsVisible(false);
      setTimeout(() => setErrorState(null), 300);
    }, 4000);
  };

  const setError = (errorData: ErrorData) => show(errorData, 'error');
  const setSuccess = (successData: SuccessData) => show(successData, 'success');

  const clearError = () => {
    setIsVisible(false);
    setTimeout(() => setErrorState(null), 300);
  };

  const isSuccess = kind === 'success';
  const borderColor = isSuccess ? colors.primary.green : colors.primary.warning;
  const IconComp = isSuccess ? CheckCircleIcon : ErrorOutlineIcon;

  return (
    <ErrorContext.Provider value={{ setError, clearError, setSuccess }}>
      {children}

      <Slide direction="right" in={isVisible} mountOnEnter unmountOnExit>
        <Box
          sx={{
            position: 'fixed',
            bottom: 24,
            left: 24,
            zIndex: 9999,
            maxWidth: 400,
            minWidth: 320,
            bgcolor: colors.background.primary,
            border: `2px solid ${borderColor}`,
            borderRadius: 2,
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
            p: 2,
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1.5,
          }}
        >
          <IconComp
            sx={{
              color: borderColor,
              fontSize: 24,
              flexShrink: 0,
            }}
          />

          <Box sx={{ flex: 1, minWidth: 0 }}>
            {error?.title && (
              <Typography
                variant="subtitle2"
                sx={{
                  color: colors.primary.white,
                  fontWeight: 600,
                  mb: 0.5,
                }}
              >
                {error.title}
              </Typography>
            )}

            <Typography
              variant="body2"
              sx={{
                color: colors.primary.white,
                fontSize: '0.875rem',
                lineHeight: 1.4,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
              }}
            >
              {error?.message}
            </Typography>
          </Box>

          <IconButton
            onClick={clearError}
            size="small"
            sx={{
              color: colors.primary.white,
              flexShrink: 0,
              '&:hover': {
                bgcolor: colors.background.secondary,
              },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </Slide>
    </ErrorContext.Provider>
  );
};
